// Build a multi-page PDF report with native charts (values printed as labels,
// so no hover is needed) plus tables, and download it.
// tables: [{ heading, columns:[{key,label,align,fmt?}], rows, chart?: 'bar'|'line' }]
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CHART_COLORS } from '../config/constants';

const fmtNum = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const truncate = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

// Horizontal bar chart with name + value labels. Returns the height used.
function drawBarChart(doc, x, y, w, rows) {
  const barH = 13;
  const gap = 9;
  const labelW = 120;
  const valW = 60;
  const chartX = x + labelW;
  const chartW = w - labelW - valW;
  const maxVal = Math.max(1, ...rows.map((r) => Number(r.total) || 0));
  rows.forEach((r, i) => {
    const ry = y + i * (barH + gap);
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(truncate(String(r.name), 24), chartX - 6, ry + barH - 3, { align: 'right' });
    const len = Math.max(1, (Number(r.total) / maxVal) * chartW);
    const [cr, cg, cb] = hexToRgb(CHART_COLORS[i % CHART_COLORS.length]);
    doc.setFillColor(cr, cg, cb);
    doc.roundedRect(chartX, ry, len, barH, 2, 2, 'F');
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(8);
    doc.text(fmtNum(r.total), chartX + len + 5, ry + barH - 3);
  });
  return rows.length * (barH + gap) + 4;
}

export function exportPdf(filename, { title, filters, kpis, tables }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 40;
  const contentW = doc.internal.pageSize.getWidth() - marginX * 2;
  const pageH = doc.internal.pageSize.getHeight();
  let y = 48;
  const ensure = (need) => { if (y + need > pageH - 40) { doc.addPage(); y = 48; } };

  // Title (bold) + brand rule
  doc.setFont(undefined, 'bold');
  doc.setFontSize(18);
  doc.setTextColor(15, 35, 72);
  doc.text(title || 'CysOps — Usage Report', marginX, y);
  doc.setFont(undefined, 'normal');
  y += 10;
  doc.setDrawColor(31, 116, 240);
  doc.setLineWidth(1.5);
  doc.line(marginX, y, marginX + contentW, y);
  doc.setLineWidth(0.2);
  y += 18;

  doc.setFontSize(9.5);
  doc.setTextColor(100);
  (filters || []).forEach((line) => { doc.text(line, marginX, y); y += 13; });
  y += 8;

  if (kpis && kpis.length) {
    autoTable(doc, {
      startY: y,
      head: [kpis.map((k) => k.label)],
      body: [kpis.map((k) => k.value)],
      theme: 'grid',
      styles: { fontSize: 10, halign: 'center' },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  (tables || []).forEach(({ heading, columns, rows, chart }) => {
    if (!rows || !rows.length || !columns || !columns.length) return;
    const chartH = chart === 'bar' ? rows.length * 22 + 10 : 0;
    ensure(chartH + 40);

    doc.setFont(undefined, 'bold');
    doc.setFontSize(12.5);
    doc.setTextColor(15, 35, 72);
    doc.text(heading, marginX, y);
    doc.setFont(undefined, 'normal');
    y += 7;
    doc.setDrawColor(219, 226, 239);
    doc.setLineWidth(0.8);
    doc.line(marginX, y, marginX + contentW, y);
    doc.setLineWidth(0.2);
    y += 12;

    if (chart === 'bar') y += drawBarChart(doc, marginX, y, contentW, rows);
    y += 8;

    autoTable(doc, {
      startY: y,
      head: [columns.map((c) => c.label)],
      body: rows.map((r) => columns.map((c) => (c.fmt ? c.fmt(r[c.key]) : r[c.key]))),
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      columnStyles: columns.reduce((acc, c, i) => {
        if (c.align === 'right') acc[i] = { halign: 'right' };
        return acc;
      }, {}),
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 22;
  });

  // Footer: brand mark + page numbers on every page.
  const pageCount = doc.internal.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('CysOps — Reseller Analytics', marginX, pageH - 20);
    doc.text(`Page ${p} of ${pageCount}`, pageW - marginX, pageH - 20, { align: 'right' });
  }

  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}
